/**
 * Create Outline cost estimate (#617). Planning math only — labeled as an
 * estimate, never billed against. Typical call counts follow the real
 * generate_outline_tree walk: one act batch, then one sequence call per
 * typical act, then one scene call per typical sequence.
 */

export const TYPICAL_ACT_COUNT = 3;
export const TYPICAL_SEQS_PER_ACT = 5;

export const LAYER_TOKEN_HINTS = {
  acts: { input: 2200, output: 2000 },
  sequences: { input: 1800, output: 1400 },
  scenes: { input: 1800, output: 1600 },
};

export function typicalCallCounts(depth, actCount = TYPICAL_ACT_COUNT) {
  const n = Math.max(1, Number(actCount) || TYPICAL_ACT_COUNT);
  if (depth === 'acts') return { acts: 1, sequences: 0, scenes: 0 };
  if (depth === 'acts_sequences') return { acts: 1, sequences: n, scenes: 0 };
  if (depth === 'all_to_scenes') {
    return { acts: 1, sequences: n, scenes: n * TYPICAL_SEQS_PER_ACT };
  }
  return null;
}

export function estimateOutlineCostUsd(modelPricing, depth, actCount = TYPICAL_ACT_COUNT) {
  const prompt = Number(modelPricing?.prompt);
  const completion = Number(modelPricing?.completion);
  if (!Number.isFinite(prompt) || !Number.isFinite(completion)) return null;
  const counts = typicalCallCounts(depth, actCount);
  if (!counts) return null;
  let usd = 0;
  for (const layer of ['acts', 'sequences', 'scenes']) {
    const count = counts[layer];
    if (!count) continue;
    const tokens = LAYER_TOKEN_HINTS[layer];
    usd += count * (tokens.input * prompt + tokens.output * completion);
  }
  return usd;
}

/**
 * Two decimals once an amount reaches one cent.
 * A positive amount under one cent is "under $0.01" — never four decimals
 * on one end of a band and two on the other.
 */
export function formatEstimateUsd(usd) {
  if (usd == null || !Number.isFinite(usd)) return null;
  if (usd > 0 && usd < 0.01) return 'under $0.01';
  return `$${usd.toFixed(2)}`;
}

/**
 * One figure when both ends say the same thing.
 * A sub-cent end is "under $0.01" only on its own. Beside a higher
 * figure it becomes the one-cent end of a dollar range, so the line
 * is never "under" in front of a range.
 */
export function formatEstimateRange(lowUsd, highUsd) {
  const low = formatEstimateUsd(lowUsd);
  const high = formatEstimateUsd(highUsd);
  if (!low || !high) return null;
  if (low === high) return low;
  const lowFigure = low === 'under $0.01' ? '$0.01' : low;
  const highFigure = high === 'under $0.01' ? '$0.01' : high;
  if (lowFigure === highFigure) return 'under $0.01';
  return `${lowFigure}–${highFigure}`;
}

// Run 43: the low/high explanation showed under a single figure ("under
// $0.01"). Explain the two figures only when two are on screen.
export function bandShowsRange(band) {
  if (!band || !Number.isFinite(band.typical) || !Number.isFinite(band.worst)) return false;
  return String(formatEstimateRange(band.typical, band.worst) || '').includes('–');
}

/** "about $0.04–$0.09", or "under $0.01" with no extra "about". */
export function formatEstimateAbout(amount) {
  if (!amount) return null;
  if (String(amount).startsWith('under ')) return amount;
  return `about ${amount}`;
}

export function pricingFromCatalog(catalog, modelId) {
  if (!modelId || !Array.isArray(catalog)) return null;
  const entry = catalog.find((m) => m.id === modelId);
  return entry?.pricing || null;
}
